import { createFileRoute, Link } from '@tanstack/react-router';
import { flattenData } from '@interxfi/api-client';
import {
  useIxmUserauthV1OrganizationQueryServiceOrganizationGet,
  useIxmUserauthV1RealmAccessQueryServiceRealmAccessList,
} from '@interxfi/api-client/generated/hooks';
import { useGetProgram } from '@interxfi/api-client/program/get-program';
import { DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE, PageLayout } from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { ActionTile } from '@interxfi/design-system/components/action-tile';
import { Grid } from '@interxfi/design-system/components/alignment/grid';
import { Skeleton } from '@interxfi/design-system/components/loading/skeleton';
import CompanyIcon from '@interxfi/design-system/icons/react/icon-company';
// import DocIcon from '@interxfi/design-system/icons/react/icon-doc';
import SettingsIcon from '@interxfi/design-system/icons/react/icon-settings';
import {
  IxmUserauthV1RealmAccessQueryServiceRealmAccessListFilterableFields,
  type IxmUserauthV1RealmAccessQueryServiceRealmAccessListRealmAccessListRequest,
  IxmUserauthV1RealmAccessStatus,
} from '@interxfi/types/generated/api';
import { getOrganizationDetailPath } from '../../../../../../../../features/userauth/organization/userauth-organization-routing';
// import { ProgramBrandingSwatchIcon } from '../../../../../../../../features/userauth/program/branding/swatch-icon/program-branding-swatch-icon';
import { ProgramPageHeader } from '../../../../../../../../features/userauth/program/program-page-header';
import { getProgramConfigurationPath } from '../../../../../../../../features/userauth/program/program-routing';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../../../../util/error-handler';

export const Route = createFileRoute('/_app/_platform/_context-required/program/_program/$programId/_program/')({
  component: ProgramDetail,
});

function buildRealmAccessListRequest(programId: string): IxmUserauthV1RealmAccessQueryServiceRealmAccessListRealmAccessListRequest {
  return {
    query: {
      filters: [
        { field: { name: IxmUserauthV1RealmAccessQueryServiceRealmAccessListFilterableFields.TenantId, type: { value: programId } } },
        {
          field: {
            name: IxmUserauthV1RealmAccessQueryServiceRealmAccessListFilterableFields.Status,
            type: { value: IxmUserauthV1RealmAccessStatus.Active },
          },
        },
      ],
    },
  };
}

const actionTileGridColumns = {
  sm: 1,
  md: 2,
} as const;

const actionTileStyles = {
  height: 140,
} as const;

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to load program', 'view this program');

function ProgramDetail() {
  const { programId } = Route.useParams();
  const request = { programId } as const;
  const { error } = useGetProgram(request);
  useErrorHandler(error, errorMessageGetter);
  const { data: programAccessResponse } = useIxmUserauthV1RealmAccessQueryServiceRealmAccessList(buildRealmAccessListRequest(programId));
  const organizationId = flattenData(programAccessResponse, 'realmAccess')?.find((a) => a.organizationId)?.organizationId;
  const { data: organizationResponse, isPending: organizationIsPending } = useIxmUserauthV1OrganizationQueryServiceOrganizationGet(
    organizationId ? { organizationId } : undefined,
  );

  return (
    <PageLayout gap={7} maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
      <PageLayout.Section noBorder>
        <ProgramPageHeader programId={programId} />
      </PageLayout.Section>

      <PageLayout.Section>
        <Grid autoColumns={actionTileGridColumns}>
          <ActionTile
            illustration={<SettingsIcon aria-hidden />}
            LinkElement={Link}
            size="medium"
            style={actionTileStyles}
            subtitle="Manage program details and controls."
            title="Program Configuration"
            to={getProgramConfigurationPath(programId)}
            tone="neutral"
          />

          <ActionTile
            illustration={<CompanyIcon aria-hidden />}
            LinkElement={Link}
            size="medium"
            style={actionTileStyles}
            subtitle={
              <span>
                Manage{' '}
                {organizationResponse?.organization?.data?.profile?.name ??
                  (organizationIsPending ? <Skeleton charCount={12} type="line" /> : 'organization')}{' '}
                and its users.
              </span>
            }
            title="Organization"
            to={getOrganizationDetailPath(organizationId)}
            tone="primary"
          />

          {/*<ActionTile
            illustration={
              <ProgramBrandingSwatchIcon variant="tertiary">
                <DocIcon aria-hidden width={16} />
              </ProgramBrandingSwatchIcon>
            }
            LinkElement={Link}
            size="medium"
            style={actionTileStyles}
            subtitle="Customize account statements and trade confirmations."
            title="Document Designer"
            to={getProgramBrandingConfigurationPath(programId)}
            tone="tertiary"
          />*/}
        </Grid>
      </PageLayout.Section>
    </PageLayout>
  );
}
